import React, {
  FunctionComponent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import Modal from '../../../modal/Modal';
import { SelectOption } from '../../../select/Select';
import Validationtip from '../../../validationtip/Validationtip';
import InputFieldText from '../../../input/floating-label/input-field/InputFieldText';
import { START_WITH } from '../../../../enum/FilterTypen';
import {
  ARROW_DOWN,
  ARROW_UP,
  BACKSPACE,
  ENTER,
  ESCAPE,
  TAB,
} from '../../../../utils/constants';

type TableCellSelectProps = {
  name: string;
  tableName?: string;
  rowIndex: number;
  startingValue?: string;
  selectData: SelectOption[];
  selectedOptionId: number;
  onChangeHandler: (
    targetName: string,
    targetValue: any,
    rowIndex: number
  ) => void;
  className?: string;
  onBlurHandler?: () => void;
  headerTitle?: string;
  filterList?: boolean;
  filterType?: string;
  readonly?: boolean;
};

const TableCellSelect: FunctionComponent<TableCellSelectProps> = (
  props
): ReactElement => {
  /*
   *  je nach Anzahl der Optionen des Selects, muss das modal
   *  in seine Größe variieren, hierfür werden mehrere Konstanten gebraucht.
   *  Da durch die Größe auch die Position (top, left) beeinflusst werden,
   *  fließen diese Konstanten auch in die Berechnung des Offsets mit ein.
   *  Die Werte korrelieren teilweise mit den verschiedenen height/width Werten
   *  in _TableCellSelect.scss bzw. _TableCell.scss
   */

  // _TableCellSelect.scss -> .select-modal-list { height } + 10px,
  // sonst überlappt der Scrollbalken die runde Ecke des border-radius
  const SELECT_MODAL_LIST_MAX_HEIGHT = 245;

  const SELECT_MODAL_LIST_ITEM_HEIGHT = 35; // _TableCellSelect.scss -> .select-modal-list > li { height }
  const SELECT_MODAL_LIST_WIDTH = 300; // kein Pendant in scss, arbiträr gewählte Breite
  const TABLE_CELL_HEIGHT = 42; // _TableCell.scss -> td { height }

  const inputRef = React.useRef() as React.MutableRefObject<HTMLInputElement>;

  const [filteredList, setFilteredList] = useState<SelectOption[]>(
    props.selectData
  );
  const [selectedOptionTitle, setSelectedOptionTitle] = useState(
    props.startingValue ?? ''
  );

  const [offsetLeft, setOffsetLeft] = useState<number>(0);
  const [offsetTop, setOffsetTop] = useState<number>(0);
  const [modalHeight, setModalHeight] = useState<number>(0);

  const tableCellSelect = useRef<HTMLDivElement>(null);
  const [isListOpen, setIsListOpen] = useState(false);
  const [focusSelectId, setFocusSelectId] = useState<number | undefined>(-1);
  const focusedLiRef = useRef<HTMLLIElement>(null);

  const optionCount = () => {
    return filteredList.length;
  };

  const focusInputField = () => {
    inputRef?.current?.focus();
  };

  /*
   Berechnung des Offsets des Modals
   Durch verschieden Effekte wie Scrollen/Fenster verkleinern oder vergrößern
   wird die Position des Selects verschoben. Soll das modal immer an der aktuellen
   Position des Selects erscheinen, muss das Offset immer aktuell berechnet werden.
   Momentan passiert dies, sobald sich die Liste des Selects öffnen soll.

   evtl TODO: Beim Öffnen der Liste sieht man manchmal für eine sehr kurze Zeit
              die Liste an ihrer vorherigen Position. Potentielle Lösung:
              Berechnen des Offsets bei Scroll- oder Resize-Events.
              Das ist deutlich komplexer und fehleranfälliger, sollte aber
              diese ghosting Effekte beim Öffnen der Liste beseitigen.
   */
  useEffect(() => {
    if (isListOpen) {
      setOffsetLeft(calculateLeftOffset());
      setOffsetTop(calculateTopOffset());
    }
  }, [modalHeight, isListOpen]);

  const calculateLeftOffset = () => {
    return tableCellSelect.current
      ? tableCellSelect.current.getBoundingClientRect().left +
          SELECT_MODAL_LIST_WIDTH / 2
      : 0;
  };

  const calculateTopOffset = () => {
    const MODAL_MARGIN_BOTTOM = 20;
    let offsetTop = tableCellSelect.current
      ? tableCellSelect.current.getBoundingClientRect().top +
        modalHeight / 2 +
        TABLE_CELL_HEIGHT
      : 0;
    // nach unten zu wenig Platz? Dann klapp die Liste nach oben auf
    if (
      offsetTop + modalHeight >
      (window.innerHeight - MODAL_MARGIN_BOTTOM ||
        document.documentElement.clientHeight - MODAL_MARGIN_BOTTOM)
    ) {
      offsetTop -= modalHeight + TABLE_CELL_HEIGHT;
    }
    return offsetTop;
  };

  const onChange = (option: SelectOption) => {
    props.onChangeHandler(
      props.name,
      selectedOption(option.elementId),
      props.rowIndex
    );
  };

  const selectedOption = (elementId: number): SelectOption | undefined => {
    return props.selectData?.find(
      selectModel => selectModel.elementId === elementId
    );
  };

  const closeList = () => {
    setIsListOpen(false);
  };

  const updateOptionTitle = () => {
    if (props.selectedOptionId !== undefined) {
      const option = props.selectData.find(
        option => option.id == props.selectedOptionId
      );
      if (option) {
        setSelectedOptionTitle(option.value);
      }
    } else {
      setSelectedOptionTitle('');
    }
  };

  useEffect(() => {
    updateOptionTitle();
  }, [props.selectedOptionId]);

  useEffect(() => {
    focusedLiRef?.current?.focus();
  }, [focusSelectId]);

  useEffect(() => {
    if (!isListOpen) return;
    window.addEventListener('click', closeList);
    focusInputField();
    setFocusSelectId(-1);
    return () => window.removeEventListener('click', closeList);
  }, [isListOpen]);

  useEffect(() => {
    setModalHeight(
      optionCount() * SELECT_MODAL_LIST_ITEM_HEIGHT <
        SELECT_MODAL_LIST_MAX_HEIGHT
        ? optionCount() * SELECT_MODAL_LIST_ITEM_HEIGHT
        : SELECT_MODAL_LIST_MAX_HEIGHT
    );
  }, [filteredList]);

  useEffect(() => {
    if (props.filterList && selectedOptionTitle && selectedOptionTitle !== '') {
      const selectOptions = filterSelectData();
      setFilteredList(selectOptions);
    }
  }, [selectedOptionTitle]);

  const toggleList = () => {
    if (!props.readonly) {
      setFilteredList(props.selectData);
      setIsListOpen(!isListOpen);
      focusedLiRef.current?.focus();
    }
  };

  const setOption = (option: SelectOption) => {
    setSelectedOptionTitle(option.value);
    !!props.onChangeHandler && onChange(option);
    !!props.onBlurHandler && props.onBlurHandler();
    toggleList();
  };

  const isCurrentOptionSelected = (option: SelectOption) => {
    return props.selectedOptionId === option.id;
  };

  const handleKeyDown = (e: React.KeyboardEvent, option: SelectOption) => {
    if (e.key === ENTER) {
      setOption(option);
    } else if (e.key === ARROW_UP || (e.key === TAB && e.shiftKey)) {
      e.preventDefault();
      focusPreviousItem();
    } else if (e.key === ARROW_DOWN || (e.key === TAB && !e.shiftKey)) {
      e.preventDefault();
      focusNextItem();
    } else if (e.key === ESCAPE) {
      setIsListOpen(false);
    }
  };

  const isLastChild = (index: number) => {
    return index + 1 === optionCount();
  };

  const filterSelectData = () => {
    return props.filterType === START_WITH
      ? props.selectData.filter(
          option =>
            selectedOptionTitle === '' ||
            option.value
              .toLowerCase()
              .startsWith(selectedOptionTitle?.toLowerCase())
        )
      : props.selectData.filter(
          option =>
            selectedOptionTitle === '' ||
            option.value
              .toLowerCase()
              .includes(selectedOptionTitle?.toLowerCase())
        );
  };

  const onChangeFilterInputHandler = (value: string) => {
    if (!value) {
      props.onChangeHandler &&
        props.onChangeHandler(props.name, undefined, props.rowIndex);
      setSelectedOptionTitle('');
      setFilteredList(props.selectData);
    } else {
      props.filterList && setFilteredList(filterSelectData());
      setIsListOpen(true);
      setSelectedOptionTitle(value);
    }
  };

  const onKeyDownHandler = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case ENTER:
        toggleList();
        break;
      case BACKSPACE:
        if (props.selectedOptionId && selectedOptionTitle) {
          setSelectedOptionTitle('');
          onChangeFilterInputHandler('');
        }
        break;
      case ESCAPE:
        setIsListOpen(false);
        break;
      case ARROW_DOWN:
      case ARROW_UP:
      case TAB:
        e.preventDefault();
        !isListOpen && setIsListOpen(true);
        focusNextItem();
        break;
    }
  };

  const focusNextItem = () => {
    if (focusSelectId !== undefined) {
      if (
        filteredList.indexOf(
          filteredList.filter(item => item.elementId === focusSelectId)[0]
        ) +
          1 !==
        filteredList.length
      ) {
        const elementId =
          filteredList[
            filteredList.indexOf(
              filteredList.filter(item => item.elementId === focusSelectId)[0]
            ) + 1
          ]?.elementId;
        setFocusSelectId(elementId);
      }
    }
  };

  const focusPreviousItem = () => {
    //Die ursprüngliche Bedingung war focusSelectId && focusSelectId !== 0, da ist der Fall mit 0 dann durch die implizite Typkonvertierung in der ersten Bedingung inklusive
    if (!!focusSelectId) {
      if (
        filteredList.indexOf(
          filteredList.filter(item => item.elementId === focusSelectId)[0]
        ) -
          1 >=
        0
      ) {
        setFocusSelectId(
          filteredList[
            filteredList.indexOf(
              filteredList.filter(item => item.elementId === focusSelectId)[0]
            ) - 1
          ]?.elementId
        );
      }
    }
  };

  const onBlurHandler = () => {
    if (!props.selectedOptionId) {
      setSelectedOptionTitle('');
    }
  };

  // errrechnet die Breite des Modals anhand des längsten Eintrages
  const calcModalWidth = () => {
    let maxLength = 35;
    filteredList.map(function (item) {
      maxLength = item.value.length > maxLength ? item.value.length : maxLength;
    });

    return maxLength * 8.5 + 'px';
  };

  return (
    <div
      className={`form-group select-wrapper table-cell-select ${
        props.className ?? ''
      }`}
    >
      <Validationtip
        fieldName={
          props.tableName && props.name
            ? props.tableName + '[' + props.rowIndex + '].' + props.name
            : ''
        }
      >
        {/* HEADER START */}
        <div
          className='select-header'
          tabIndex={0}
          onClick={toggleList}
          ref={tableCellSelect}
          aria-readonly={props.readonly}
          onKeyDown={e => !props.filterList && onKeyDownHandler(e)}
          onBlur={!props.filterList ? onBlurHandler : undefined}
        >
          {/* Anzeige ausgewaehltes Element */}
          <div
            className={`select-header-title ${
              !!selectedOptionTitle && 'selected'
            }`}
          >
            {props.filterList ? (
              <InputFieldText
                onChangeHandler={onChangeFilterInputHandler}
                value={selectedOptionTitle}
                label={props.headerTitle}
                readonly={props.readonly}
                onKeyDownHandler={e => onKeyDownHandler(e)}
                onBlurHandler={onBlurHandler}
                ref={inputRef}
              />
            ) : props.selectedOptionId !== undefined ? (
              `${selectedOptionTitle}`
            ) : (
              ''
            )}
          </div>

          {isListOpen ? (
            <i className='select-arrow fas fa-angle-up' />
          ) : (
            <i className='select-arrow fas fa-angle-down' />
          )}
        </div>
        {/* HEADER END */}
        {/* MODAL LIST START */}
        <Modal
          show={isListOpen}
          closeHandler={closeList}
          titel={props.name}
          width={calcModalWidth()}
          height={`${modalHeight}px`}
          posX={`${offsetLeft}px`}
          posY={`${offsetTop}px`}
          withoutHeader
          classes={'fake-modal'}
        >
          <ul
            className={'select-modal-list'}
            style={{
              overflowY: `${
                modalHeight < SELECT_MODAL_LIST_MAX_HEIGHT ? 'hidden' : 'scroll'
              }`,
            }}
          >
            {filteredList?.map((option, index) => (
              <li
                key={option.elementId}
                ref={
                  focusSelectId === option.elementId ? focusedLiRef : undefined
                }
                className={`select-list-option ${
                  isCurrentOptionSelected(option) && 'bold'
                }`}
                onClick={() => {
                  setOption(option);
                }}
                onKeyDown={e => {
                  handleKeyDown(e, option);
                }}
                tabIndex={0}
              >
                {option.value}
              </li>
            ))}
          </ul>
        </Modal>
        {/* MODAL LIST END */}
      </Validationtip>
    </div>
  );
};

TableCellSelect.defaultProps = {
  readonly: false,
  filterList: true,
  filterType: START_WITH,
};

export default TableCellSelect;
